import * as React from 'react'
import { injectIntl, type WrappedComponentProps } from 'react-intl'
import type moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import Caption from 'LuxKit/Typography/Caption'
import CalendarGrid from './CalendarGrid'
import memoize from 'lib/memoize/memoize'

const CalendarContainer = styled.div`
  > * + * {
    margin-top: ${rem(4)};
  }
`

const WeekHeader = styled(Caption)`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

const RelativeBlock = styled.div`
  position: relative;
`

interface Props {
  className?: string
  // month and year this calendar is for
  month: number
  year: number
  // minimum and maximum dates that are selectable
  minDate?: Date
  maxDate?: Date
  // the selected dates
  startDate?: Date
  endDate?: Date
  // Labels that'll appear in the start/end days
  startLabel?: string
  endLabel?: string
  onDayClick: (day: moment.Moment) => void
}
const getStartOfMonth = memoize((month: number, year: number) => new Date(year, month), (month, year) => `${month}${year}`)

// Class used to prevent onDayClick reference from changing on each click
class Calendar extends React.PureComponent<React.PropsWithChildren<Props & WrappedComponentProps>> {
  onDayClick = (day: moment.Moment) => {
    this.props.onDayClick(day)
  }

  render() {
    const {
      intl,
      className,
      startDate,
      endDate,
      minDate,
      maxDate,
      startLabel,
      endLabel,
      month,
      year,
    } = this.props

    const startOfMonth = getStartOfMonth(month, year)

    const sundayFull = intl.formatMessage({
      id: '9iivdXkUw2drku628wso',
      defaultMessage: 'Sunday',
      description: 'Full name for Sunday in calendar week header in the calendar picker',
    })
    const mondayFull = intl.formatMessage({
      id: 'm49b3Rl6gdOPh2cG9zxC',
      defaultMessage: 'Monday',
      description: 'Full name for Monday in calendar week header in the calendar picker',
    })
    const tuesdayFull = intl.formatMessage({
      id: 'U0kB7ll2jMxjcJbu3t4U',
      defaultMessage: 'Tuesday',
      description: 'Full name for Tuesday in calendar week header in the calendar date picker',
    })
    const wednesdayFull = intl.formatMessage({
      id: 'bs_4M2IEMrmmV4Nne9WA',
      defaultMessage: 'Wednesday',
      description: 'Full name for Wednesday in calendar week header in the calendar date picker',
    })
    const thursdayFull = intl.formatMessage({
      id: 'j7lDWi8MC7YyyzTnxwSv',
      defaultMessage: 'Thursday',
      description: 'Full name for Thursday in calendar week header in the calendar date picker',
    })
    const fridayFull = intl.formatMessage({
      id: 'LAzZnDpOOjRLllGZNrGi',
      defaultMessage: 'Friday',
      description: 'Full name for Friday in calendar week header in the calendar picker',
    })
    const saturdayFull = intl.formatMessage({
      id: 'pbzjKOhWMNa_ny5HkMaa',
      defaultMessage: 'Saturday',
      description: 'Full name for Saturday in calendar week header in the calendar date picker',
    })

    const sundayAbbr = intl.formatMessage({
      id: 'En8ySE2GjAvLOBZZfNXp',
      defaultMessage: 'Sun',
      description: 'Abbreviated name for Sunday in calendar week header',
    })
    const mondayAbbr = intl.formatMessage({
      id: 'atbMBEtMVvHT2DCo6PUr',
      defaultMessage: 'Mon',
      description: 'Abbreviated name for Monday in calendar week header',
    })
    const tuesdayAbbr = intl.formatMessage({
      id: 'TcEu6j5gejUWCoznQ41K',
      defaultMessage: 'Tue',
      description: 'Abbreviated name for Tuesday in calendar week header',
    })
    const wednesdayAbbr = intl.formatMessage({
      id: 'QDw3l7mWrdEZRB8xmJun',
      defaultMessage: 'Wed',
      description: 'Abbreviated name for Wednesday in calendar week header',
    })
    const thursdayAbbr = intl.formatMessage({
      id: 'nc5kku8lIZht9AH3Zs8d',
      defaultMessage: 'Thu',
      description: 'Abbreviated name for Thursday in calendar week header',
    })
    const fridayAbbr = intl.formatMessage({
      id: 'X7hmrIKWsYX87SpNUC6p',
      defaultMessage: 'Fri',
      description: 'Abbreviated name for Friday in calendar week header',
    })
    const saturdayAbbr = intl.formatMessage({
      id: 'Z9I6hXp_rGt2xqGhggYP',
      defaultMessage: 'Sat',
      description: 'Abbreviated name for Saturday in calendar week header',
    })

    return (
      <CalendarContainer className={className}>
        <WeekHeader variant="medium" align="start" colour="light-foreground-alt">
          <abbr title={sundayFull}>{sundayAbbr}</abbr>
          <abbr title={mondayFull}>{mondayAbbr}</abbr>
          <abbr title={tuesdayFull}>{tuesdayAbbr}</abbr>
          <abbr title={wednesdayFull}>{wednesdayAbbr}</abbr>
          <abbr title={thursdayFull}>{thursdayAbbr}</abbr>
          <abbr title={fridayFull}>{fridayAbbr}</abbr>
          <abbr title={saturdayFull}>{saturdayAbbr}</abbr>
        </WeekHeader>
        <RelativeBlock>
          <CalendarGrid
            minDate={minDate}
            maxDate={maxDate}
            startOfMonth={startOfMonth}
            onDayClick={this.onDayClick}
            startDate={startDate}
            endDate={endDate}
            startLabel={startLabel}
            endLabel={endLabel}
          />
        </RelativeBlock>
      </CalendarContainer>
    )
  }
}

export default injectIntl(Calendar)
